import React, { useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { StepsButton } from "./StepsButtons";
import { useSelector, useDispatch } from "react-redux";
import { setActiveStep } from "../redux/stepperROWSlice";
import type { AppDispatch } from "@store/store";
import type { RootReducerType } from "@store/store";
import {
  getDocumentsListThunk,
  getFileUploadSignedURLThunk,
  postSaveUploadedFileThunk,
  getUploadedFileByIdThunk,
  getDeleteSignedURLThunk,
  submitDocumentThunk,
  deleteFileByIdThunk,
  getFileSignedURLThunk,
} from "../redux/thunk/documentThunk";
import { deleteFileOnAwsAPI } from "../../../utils/s3Upload";
import { UploadDocumentProps } from "../types";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { ImagePreviewGridRow } from "@modules/application-section-row/components/ImagePreviewGridRow";
import { resetApplicationSubmitted } from "@modules/application-section/application-details/redux/applicationSubmittedSlice";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import CBuDInfo from "./modal/CBuDInfo";

const UploadDocumentsROW: React.FC<UploadDocumentProps> = ({ collapsed }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { activeStep } = useSelector(
    (state: RootReducerType) => state.stepperROW,
  );
  const { category_id, access_id } = useSelector(
    (state: RootReducerType) => state.amenitiesROW,
  );
  const [allDocuments, setAllDocuments] = React.useState<any[]>([]);
  const navigate = useNavigate();
  const [progress, setProgress] = React.useState(0);
  const [allUploadedFiles, setAllUploadedFiles] = React.useState<any[]>([]);
  const [refreshImagesFlag, setRefreshImagesFlag] = React.useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const applicationEditId = useSelector(
    (state: RootReducerType) => state.applicationDashboard?.id,
  );

  const applicationID = access_id || applicationEditId;

  const fetchAndMergeDocuments = async () => {
    const documentListData = await dispatch(
      getDocumentsListThunk({
        category_id: category_id,
        access_id: applicationEditId || access_id,
        document: "file_upload",
      }),
    );
    const baseDocs = documentListData.payload.data || [];
    const uploadedFileById = await dispatch(
      getUploadedFileByIdThunk({
        access_id: applicationEditId || access_id,
        category_id: category_id,
        document: "file_upload",
      }),
    );
    const uploadedFiles = uploadedFileById.payload.data?.files || [];
    setProgress(uploadedFileById.payload.data?.completionPercentage || 0);
    setAllUploadedFiles(uploadedFiles);
    const mergedDocs = baseDocs.map((doc: any) => {
      const uploadedFile =
        uploadedFiles.length > 0 &&
        uploadedFiles.find((file: any) => file.document_id === doc.document_id);
      return uploadedFile ? { ...doc, uploadedFile } : doc;
    });
    setAllDocuments(mergedDocs);
    setRefreshImagesFlag((prev) => !prev);
  };

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: doc_name,
      dispatch,
      getSignedUrlThunk: getFileSignedURLThunk,
    });
  };

  function getTruncatedFileName(fileName: string, maxLength = 15) {
    const lastDot = fileName?.lastIndexOf(".");
    if (lastDot === -1) return fileName;
    const name = fileName?.slice(0, lastDot);
    const ext = fileName?.slice(lastDot);
    const truncated =
      name?.length > maxLength ? name?.slice(0, maxLength) + "..." : name;
    return truncated + ext;
  }

  React.useEffect(() => {
    fetchAndMergeDocuments();
  }, [dispatch, category_id, access_id]);

  // Split documents by type
  const filterpdfList = allDocuments?.filter(
    (doc) => doc.document_type === "FILE_UPLOAD" && doc.media_type === "pdf",
  );

  const filterImageList = allDocuments?.filter(
    (doc) => doc.document_type === "FILE_UPLOAD" && doc.media_type === "image",
  );

  const filterdrwList = allDocuments?.filter(
    (doc) => doc.document_type === "FILE_UPLOAD" && doc.media_type === "drw",
  );

  React.useEffect(() => {
    if (!Array.isArray(filterImageList) || filterImageList.length === 0) return;

    const lastImagePath =
      filterImageList[filterImageList.length - 1]?.uploadedFile?.upload_path;
    if (!lastImagePath) return;

    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [
    filterImageList?.[filterImageList.length - 1]?.uploadedFile?.upload_path,
  ]);

  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [location.hash]);

  // Helper function to handle file upload
  const handleFileUpload = async (
    file: File,
    doc: any,
    fileInput: HTMLInputElement,
  ) => {
    try {
      if (file.size > 21 * 1024 * 1024) {
        toast.error("File size must be less than 20 MB");
        return false;
      }

      const fileUploadURL = await dispatch(
        genericUploadThunk({
          file,
          moduleType: "row",
        }),
      );

      if (!fileUploadURL?.payload?.success) {
        const fileUploadURLDetails =
          fileUploadURL?.payload?.response?.data?.details ||
          fileUploadURL?.payload?.details;

        toast.error(
          Array.isArray(fileUploadURLDetails)
            ? fileUploadURLDetails?.[0]
            : typeof fileUploadURLDetails === "string"
              ? fileUploadURLDetails
              : "Something Went Wrong!",
        );
        return false;
      }

      if (!productionCheck) {
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: fileUploadURL.payload.data.signedUrl,
            file,
            contentType: file.type,
            uploadInstructions: fileUploadURL.payload.data.uploadInstructions,
          }),
        ).unwrap();

        if (!uploadSuccess) {
          toast.error("File upload to S3 failed");
          return false;
        }
      }

      await dispatch(
        postSaveUploadedFileThunk({
          application_id: applicationID,
          document_id: doc.document_id,
          document_type: doc.document_type,
          fileName: fileUploadURL.payload.data.fileName,
          file_name: file.name,
          originalFileName: fileUploadURL.payload.data.fileName,
          objectKey: fileUploadURL.payload.data.objectKey,
          moduletype: "row",
          isUndertaking: false,
        }),
      );

      await fetchAndMergeDocuments();
      fileInput.value = "";
      return true;
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file");
      return false;
    }
  };

  // Helper function to handle file deletion
  const handleDeleteFile = async (doc: any) => {
    try {
      const deleteSignURL = await dispatch(
        getDeleteSignedURLThunk(doc.uploadedFile.document_name),
      );
      const signedUrl = deleteSignURL.payload?.data?.signedUrl;
      if (deleteSignURL.meta.requestStatus === "fulfilled") {
        await deleteFileOnAwsAPI(signedUrl);
        await dispatch(
          deleteFileByIdThunk({
            access_id: applicationID,
            document_id: doc.uploadedFile.id,
          }),
        );
        await fetchAndMergeDocuments();
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Failed to delete file");
    }
  };

  // Render document row with file upload
  const renderDocumentRow = (doc: any, type: string) => (
    <Box
      key={doc.document_id}
      className={`document-row ${doc.index % 2 === 0 ? "even" : "odd"}`}
    >
      <Typography
        variant="body2"
        className="document-text"
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <span>
          {doc.document_name}
          {doc.upload_required && <span className="required">*</span>}
        </span>

        {doc.document_code === "CBUD_APP3" && (
          <CBuDInfo documentId={`info-${doc.document_id}`} />
        )}
      </Typography>

      <label
        htmlFor={`upload-file-${doc.document_id}`}
        className="flex items-center gap-1"
      >
        {!doc.uploadedFile ? (
          <React.Fragment>
            <input
              type="file"
              id={`upload-file-${doc.document_id}`}
              style={{ display: "none" }}
              accept={type === "image" ? ".jpg,.jpeg,.png" : ".pdf"}
              onChange={async (event) => {
                const fileInput = event.target;
                const file = fileInput.files?.[0];
                if (file) {
                  if (type === "image") {
                    const allowedTypes = ["image/jpeg", "image/png"];
                    if (!allowedTypes.includes(file.type)) {
                      toast.error("Only JPG, JPEG, and PNG files are allowed");
                      return;
                    }
                  }
                  await handleFileUpload(file, doc, fileInput);
                }
              }}
            />
            <IconButton
              sx={{
                background: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #D0D5DD",
                padding: "6px 8px",
                "&:hover": {
                  backgroundColor: "#E7E4F8",
                },
              }}
              className="download-btn"
              component="span"
            >
              <FileUploadOutlinedIcon className="download-icon" />
            </IconButton>
          </React.Fragment>
        ) : (
          <>
            <Typography
              className="font-medium underline cursor-pointer body-xs"
              sx={{
                color: "#757575",
                mr: 1,
                "&:hover": {
                  color: "#3A1C73",
                  textDecoration: "underline",
                },
              }}
              onClick={() => handleViewFile(doc?.uploadedFile?.document_name)}
              title={doc.file_name}
            >
              {getTruncatedFileName(doc?.uploadedFile?.file_name)}
            </Typography>
            <IconButton
              sx={{
                background: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #D0D5DD",
                padding: "6px 8px",
                mx: 1,
                "&:hover": {
                  backgroundColor: "#E7E4F8",
                },
              }}
              className="download-btn"
            >
              <RemoveRedEyeOutlinedIcon
                className="download-icon"
                onClick={() => handleViewFile(doc?.uploadedFile?.document_name)}
              />
            </IconButton>
            <IconButton
              sx={{
                background: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #D0D5DD",
                padding: "6px 8px",
                "&:hover": {
                  backgroundColor: "#E7E4F8",
                },
              }}
              className="download-btn"
            >
              <CloseIcon
                className="download-icon"
                onClick={() => handleDeleteFile(doc)}
              />
            </IconButton>
          </>
        )}
      </label>
    </Box>
  );

  const renderDocumentList = () => (
    <>
      <Box className="document-header">
        <h5 className="font-semibold body-m">Drawing Type (PDF)</h5>
        <h5 className="font-semibold body-m">Action</h5>
      </Box>
      <Box className="document-list">
        {filterdrwList?.map((doc, index) => renderDocumentRow(doc, "pdf"))}
      </Box>

      <Divider sx={{ marginY: 2 }} />

      <Box className="document-header">
        <h5 className="font-semibold body-m">Document Type (PDF)</h5>
        <h5 className="font-semibold body-m">Action</h5>
      </Box>
      <Box className="document-list">
        {filterpdfList?.map((doc, index) => renderDocumentRow(doc, "pdf"))}
      </Box>
    </>
  );

  return (
    <>
      <Box className="upload-document-wrapper" ref={containerRef}>
        <div className="progress-bar">
          <h3 className="stepper-hd w-full body-m flex items-center justify-between">
            Upload Documents
            <Box position="relative" display="flex">
              <CircularProgress
                variant="determinate"
                value={progress}
                size={40}
                thickness={4}
                sx={{
                  "& .MuiCircularProgress-circle": {
                    stroke: "#2E844A",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "50%",
                    border: "4px solid #CFDAE7",
                    zIndex: -1,
                  },
                }}
              />
              <Box
                top={0}
                left={0}
                bottom={0}
                right={0}
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="caption"
                  component="div"
                  color="textSecondary"
                >
                  {`${progress || 0}%`}
                </Typography>
              </Box>
            </Box>
          </h3>
        </div>

        {collapsed ? (
          <Grid className="collapsed-grid">{renderDocumentList()}</Grid>
        ) : (
          <Box className="upload-document-container">
            {renderDocumentList()}
          </Box>
        )}

        <Box mx={2} id="upload-plot-pictures">
          <h3
            className="stepper-hd w-full body-m flex items-center"
            style={{ paddingLeft: "2px" }}
          >
            Upload Plot Pictures<span className="required">*</span>
          </h3>
          {filterImageList?.map((doc, index) => (
            <Box key={doc.document_id}>
              <Box sx={{ mt: 1 }}>
                <ImagePreviewGridRow
                  refreshTrigger={refreshImagesFlag}
                  onDeleteSuccess={() => {
                    fetchAndMergeDocuments();
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  mt: 3,
                  border: "1px dashed #CFDAE7",
                  borderRadius: "8px",
                  p: 2,
                  background: "#f0edf4",
                  mb: 3,
                  "&:hover": {
                    backgroundColor: "#E7E4F8",
                    transition: "all 0.3s ease-in-out",
                    borderColor: "#3a1c73",
                  },
                }}
                component="label"
              >
                <Box component="div">
                  <input
                    type="file"
                    id={`upload-file-${doc.document_id}`}
                    hidden
                    accept=".jpg,.jpeg,.png"
                    onChange={async (event) => {
                      const fileInput = event.target;
                      const file = fileInput.files?.[0];
                      if (file) {
                        await handleFileUpload(file, doc, fileInput);
                      }
                    }}
                  />
                  <AddPhotoAlternateOutlinedIcon
                    style={{ fontSize: 24, color: "#3a1c73" }}
                  />
                </Box>
                <Typography
                  sx={{ color: "#3a1c73", fontSize: 12, cursor: "pointer" }}
                >
                  Click to add Photos
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <StepsButton
        onBack={() => dispatch(setActiveStep(activeStep - 1))}
        nextDisabled={progress !== 100}
        onNext={async () => {
          if (progress === 100) {
            const submit = await dispatch(
              submitDocumentThunk({
                access_id: applicationID,
                category_id: category_id,
              }),
            );
            if (submit.meta.requestStatus === "fulfilled") {
              if (submit.payload.data?.isComplete) {
                toast.success("Documents submitted successfully.");
                dispatch(resetApplicationSubmitted());
                navigate("/application-preview-details-row");
              }
            }
          } else {
            toast.error(
              "Please upload all required documents before proceeding.",
            );
          }
        }}
      />
    </>
  );
};

export default UploadDocumentsROW;
